import { useMemo } from 'react';
import dayjs from 'dayjs';

import { ReservationFlowPaymentMethodListTicketBook } from 'src/interfaces/reservation_flow';
import { isExpiredDate } from 'src/state-machines/booking/functions/is-expired-date';

export interface UseDisplayBehaviorParams {
  today: string; // YYYY-MM-DD で表される今日の日付
  merchantTimeZone: string;
  paymentMethodsTicketBook: ReservationFlowPaymentMethodListTicketBook;
  selectedDate?: string;
}

export interface UseDisplayBehaviorValue {
  items: {
    product: ReservationFlowPaymentMethodListTicketBook['products'][number];
  }[];
}

export const useDisplayBehavior: (
  params: UseDisplayBehaviorParams,
) => UseDisplayBehaviorValue = ({
  today,
  merchantTimeZone,
  paymentMethodsTicketBook,
  selectedDate,
}) => {
  const items = useMemo<UseDisplayBehaviorValue['items']>(() => {
    // 表示可能な回数券の抽出
    const listedProducts = paymentMethodsTicketBook.products.filter(
      (product) => {
        // 未公開は表示しない
        if (!product.is_published) {
          return false;
        }

        // パスワード保護されていないものは常に表示する
        if (!product.is_password_protected) {
          return true;
        }

        // パスワード保護されていてもリスト表示可能なものは表示する
        return product.is_password_protection_listable;
      },
    );

    // 日時選択が行われていない場合、表示可能な回数券を全て表示する
    if (typeof selectedDate === 'undefined') {
      return listedProducts.map((product) => {
        return { product };
      });
    }

    // 日時選択がされている時は、今日購入した場合に利用可能な回数券のみを表示する
    return listedProducts
      .filter((product) => {
        const expiredAt = dayjs
          .tz(today, 'YYYY-MM-DD', merchantTimeZone)
          .add(product.expiration_month, 'month')
          .format('YYYY-MM-DD');

        return !isExpiredDate({
          merchantTimeZone,
          baseDate: selectedDate,
          date: expiredAt,
        });
      })
      .map((product) => {
        return { product };
      });
  }, [today, merchantTimeZone, paymentMethodsTicketBook, selectedDate]);

  return {
    items,
  };
};
